import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { bookingsService } from '../../../services/api';
import { AlertTriangle as AlertTriangleIcon } from 'lucide-react';

/**
 * ApplyDiscountForm - Form for applying discounts to booked rooms, beds, or courses
 */
function ApplyDiscountForm({ booking, entityType, selectedItem, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [discountType, setDiscountType] = useState('PERCENTAGE');
  const [discountValue, setDiscountValue] = useState('');
  const [reason, setReason] = useState('');
  const [adminNote, setAdminNote] = useState('');

  // Calculate discount preview
  const calculateDiscount = () => {
    if (!selectedItem || !discountValue) return { amount: 0, newAmount: selectedItem?.finalAmount || 0 };

    const originalAmount = selectedItem.finalAmount || 0;
    let discountAmount = 0;

    if (discountType === 'PERCENTAGE') {
      discountAmount = (originalAmount * parseFloat(discountValue)) / 100;
    } else if (discountType === 'FIXED') {
      discountAmount = parseFloat(discountValue);
    }

    const newAmount = Math.max(0, originalAmount - discountAmount);

    return {
      amount: discountAmount,
      newAmount: newAmount,
      originalAmount: originalAmount
    };
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedItem) {
      toast.error(`Please select a ${entityType}`);
      return;
    }

    if (!discountValue || parseFloat(discountValue) <= 0) {
      toast.error('Please enter a valid discount value');
      return;
    }

    if (!reason.trim()) {
      toast.error('Please provide a reason for the discount');
      return;
    }

    const discount = calculateDiscount();
    if (discount.amount >= discount.originalAmount) {
      toast.error('Discount amount cannot exceed the original amount');
      return;
    }

    setLoading(true);
    try {
      const data = {
        discountType: discountType,
        discountValue: parseFloat(discountValue),
        reason: reason.trim(),
        adminNote: adminNote.trim() || undefined
      };

      let response;
      if (entityType === 'room') {
        response = await bookingsService.applyRoomDiscount(booking.id, selectedItem.id, data);
        toast.success('Discount applied to room successfully');
      } else if (entityType === 'bed') {
        response = await bookingsService.applyBedDiscount(booking.id, selectedItem.id, data);
        toast.success('Discount applied to bed successfully');
      } else if (entityType === 'course') {
        response = await bookingsService.applyCourseDiscount(booking.id, selectedItem.id, data);
        toast.success('Discount applied to course successfully');
      }

      if (onSuccess) {
        onSuccess(response);
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      toast.error(error.error || error.message || 'Failed to apply discount');
    } finally {
      setLoading(false);
    }
  };

  const discount = calculateDiscount();

  if (!selectedItem) {
    return (
      <div style={{ backgroundColor: '#F5F3EB', padding: '20px', borderRadius: '8px', border: '1px solid #E3DFD3', textAlign: 'center' }}>
        <p style={{ color: '#5F6B61', fontSize: '14px' }}>Select a {entityType} to apply discount</p>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#F5F3EB', padding: '20px', borderRadius: '8px', border: '1px solid #E3DFD3' }}>
      <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
        Apply Discount to {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
      </h4>

      {/* Current Item Info */}
      <div style={{ backgroundColor: '#E1E9E2', padding: '12px', borderRadius: '6px', marginBottom: '16px', border: '1px solid #1A3E2A' }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#0F2619', marginBottom: '4px' }}>
          Selected {entityType.charAt(0).toUpperCase() + entityType.slice(1)}:
        </div>
        <div style={{ fontSize: '13px', color: '#0B1D13' }}>
          {entityType === 'room' && `Room ${selectedItem.roomNumber || selectedItem.room?.roomNumber}`}
          {entityType === 'bed' && `Bed ${selectedItem.bedNumber || selectedItem.bed?.bedNumber} in Room ${selectedItem.roomNumber || selectedItem.room?.roomNumber}`}
          {entityType === 'course' && `${selectedItem.courseName || selectedItem.course?.name}`}
          <span style={{ marginLeft: '8px', fontWeight: '600' }}>
            • Current Amount: ₹{selectedItem.finalAmount}
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Discount Type */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
            Discount Type *
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <button
              type="button"
              onClick={() => setDiscountType('PERCENTAGE')}
              style={{
                padding: '12px',
                border: discountType === 'PERCENTAGE' ? '2px solid #1A3E2A' : '1px solid #D2CDBF',
                borderRadius: '6px',
                backgroundColor: discountType === 'PERCENTAGE' ? '#E1E9E2' : 'white',
                color: discountType === 'PERCENTAGE' ? '#0F2619' : '#36433B',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Percentage (%)
            </button>
            <button
              type="button"
              onClick={() => setDiscountType('FIXED')}
              style={{
                padding: '12px',
                border: discountType === 'FIXED' ? '2px solid #1A3E2A' : '1px solid #D2CDBF',
                borderRadius: '6px',
                backgroundColor: discountType === 'FIXED' ? '#E1E9E2' : 'white',
                color: discountType === 'FIXED' ? '#0F2619' : '#36433B',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Fixed Amount (₹)
            </button>
          </div>
        </div>

        {/* Discount Value */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
            Discount Value * {discountType === 'PERCENTAGE' ? '(%)' : '(₹)'}
          </label>
          <input
            type="number"
            value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
            min="0"
            max={discountType === 'PERCENTAGE' ? '100' : undefined}
            step={discountType === 'PERCENTAGE' ? '1' : '0.01'}
            required
            placeholder={discountType === 'PERCENTAGE' ? 'Enter percentage (e.g., 15)' : 'Enter amount (e.g., 500)'}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        {/* Discount Preview */}
        {discountValue && (
          <div style={{ 
            backgroundColor: '#F3E6CB', 
            padding: '12px', 
            borderRadius: '6px', 
            marginBottom: '16px',
            border: '1px solid #C9903C'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#684A1F', marginBottom: '8px' }}>
              Discount Preview:
            </div>
            <div style={{ fontSize: '13px', color: '#4F3818', lineHeight: '1.6' }}>
              <div>Original Amount: <strong>₹{discount.originalAmount}</strong></div>
              <div>Discount: <strong style={{ color: '#8E3628' }}>
                - ₹{discount.amount.toFixed(2)}
                {discountType === 'PERCENTAGE' && ` (${discountValue}%)`}
              </strong></div>
              <div style={{ borderTop: '1px solid #C9903C', marginTop: '4px', paddingTop: '4px' }}>
                New Amount: <strong style={{ color: '#163626', fontSize: '15px' }}>₹{discount.newAmount.toFixed(2)}</strong>
              </div>
              <div style={{ marginTop: '4px', fontStyle: 'italic', color: '#684A1F' }}>
                Savings: ₹{discount.amount.toFixed(2)}
              </div>
            </div>
          </div>
        )}

        {/* Reason */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
            Reason for Discount * <span style={{ fontSize: '12px', fontWeight: '400', color: '#5F6B61' }}>(required for audit)</span>
          </label>
          <select
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              // Clear admin note if changing from custom reason
              if (e.target.value !== 'custom') {
                setAdminNote('');
              }
            }}
            required
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px',
              fontSize: '14px',
              marginBottom: '8px'
            }}
          >
            <option value="">Select a reason...</option>
            <option value="Loyalty program discount">Loyalty Program Discount</option>
            <option value="Early bird discount">Early Bird Discount</option>
            <option value="Group booking discount">Group Booking Discount</option>
            <option value="Compensation for service issue">Compensation for Service Issue</option>
            <option value="Promotional offer">Promotional Offer</option>
            <option value="Long-term guest discount">Long-term Guest Discount</option>
            <option value="First-time guest discount">First-time Guest Discount</option>
            <option value="custom">Custom Reason (specify below)</option>
          </select>

          {reason === 'custom' && (
            <input
              type="text"
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Enter custom reason..."
              required
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          )}
        </div>

        {/* Admin Note */}
        {reason && reason !== 'custom' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
              Additional Notes (optional)
            </label>
            <textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              rows="2"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                resize: 'vertical'
              }}
              placeholder="Add any additional notes..."
            />
          </div>
        )}

        {/* Warning for large discounts */}
        {discountValue && ((discountType === 'PERCENTAGE' && parseFloat(discountValue) > 50) || 
          (discountType === 'FIXED' && discount.amount > discount.originalAmount * 0.5)) && (
          <div style={{ 
            backgroundColor: '#E5B3A9', 
            padding: '12px', 
            borderRadius: '6px', 
            marginBottom: '16px',
            border: '1px solid #8E3628'
          }}>
            <div style={{ fontSize: '13px', color: '#5F241B', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}><AlertTriangleIcon size="1em" aria-hidden="true" /></span>
              <span>
                <strong>Large Discount Warning:</strong> This discount exceeds 50% of the original amount. 
                Please ensure this is intentional and properly authorized.
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={{
              backgroundColor: 'white',
              color: '#36433B',
              border: '1px solid #D2CDBF',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !discountValue || !reason}
            style={{
              backgroundColor: loading || !discountValue || !reason ? '#A5A89B' : '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading || !discountValue || !reason ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Applying...' : 'Apply Discount'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ApplyDiscountForm;
